import * as React from 'react'

type Toast = { id: number; title?: string; description?: string; variant?: 'default'|'success'|'error'|'info'|'warning'; soft?: boolean; durationMs?: number }

const ToastCtx = React.createContext<{ add: (t: Omit<Toast, 'id'>) => void } | null>(null)

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [list, setList] = React.useState<Toast[]>([])

  const remove = React.useCallback((id: number) => {
    setList((s) => s.filter((i) => i.id !== id))
  }, [])

  const add = React.useCallback(
    (t: Omit<Toast, 'id'>) => {
      const id = Date.now() + Math.random()
      const duration = Math.max(1000, Math.min(20000, t.durationMs ?? 4000))
      setList((s) => [...s, { id, variant: 'default', soft: false, durationMs: duration, ...t }])
      // auto-dismiss
      setTimeout(() => {
        remove(id)
      }, duration)
    },
    [remove],
  )

  const ctxValue = React.useMemo(() => ({ add }), [add])
  return (
    <ToastCtx.Provider value={ctxValue}>
      {children}
      {/* z-index acima do Modal (z-[100000]) para garantir visibilidade */}
      <div className="fixed bottom-4 right-4 z-[100001] flex w-80 flex-col gap-2">
        {list.map((t) => (
          <div
            key={t.id}
            className={[
              'rounded-md border p-3 shadow',
              colorClasses(t.variant || 'default', !!t.soft),
            ].join(' ')}
          >
            {t.title && <div className="font-medium">{t.title}</div>}
            {t.description && <div className="text-sm text-foreground/80">{t.description}</div>}
            <button className="mt-2 text-xs underline" onClick={() => remove(t.id)}>
              Fechar
            </button>
          </div>
        ))}
      </div>
    </ToastCtx.Provider>
  )
}

export function useToast() {
  const ctx = React.useContext(ToastCtx)
  if (!ctx) throw new Error('useToast must be used within ToastProvider')
  return ctx
}

function colorClasses(variant: Toast['variant'], soft: boolean) {
  const mapBase: Record<string, { border: string; bg: string; text: string }> = {
    default: { border: 'border-border', bg: 'bg-popover', text: 'text-popover-foreground' },
    success: { border: 'border-emerald-500/30', bg: soft ? 'bg-emerald-500/10' : 'bg-emerald-600', text: soft ? 'text-emerald-600 dark:text-emerald-400' : 'text-white' },
    error: { border: 'border-red-500/30', bg: soft ? 'bg-red-500/10' : 'bg-red-600', text: soft ? 'text-red-600 dark:text-red-400' : 'text-white' },
    info: { border: 'border-sky-500/30', bg: soft ? 'bg-sky-500/10' : 'bg-sky-600', text: soft ? 'text-sky-600 dark:text-sky-400' : 'text-white' },
    warning: { border: 'border-amber-500/30', bg: soft ? 'bg-amber-500/10' : 'bg-amber-600', text: soft ? 'text-amber-600 dark:text-amber-400' : 'text-white' },
  }
  const v = mapBase[variant || 'default'] || mapBase.default
  return `${v.bg} ${v.border} ${v.text}`
}

